import { Badge, Button, Group, SimpleGrid, Stack, Text, TextInput } from "@mantine/core"
import { useEffect, useState } from "react"
import { AccountBalanceHistoryTable } from "atlas_forecasting_app/components/account_balance/account_balance_history_table"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatCurrencyFromCents, formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { CenteredMessage, ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"

export function AdminUserPage({ currentUser, userId }) {
  const isAdmin = !!currentUser?.roles?.includes("admin")
  const [user, setUser] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [amount, setAmount] = useState("")
  const [reason, setReason] = useState("")
  const [saving, setSaving] = useState(false)
  const [roleSaving, setRoleSaving] = useState(false)

  const refresh = async () => {
    if (!isAdmin) return
    setLoading(true)
    try {
      const data = await apiFetch(`/api/admin/users/${userId}`)
      setUser(data.user)
      setTransactions(data.transactions || [])
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAdmin])

  const submitBalanceAdjustment = async () => {
    if (!user || saving) return
    setSaving(true)
    try {
      const data = await apiFetch(`/api/admin/users/${user.id}/account_balance_transactions`, {
        method: "POST",
        body: { amount: amount.trim(), reason: reason.trim() },
      })
      if (data?.user) setUser(data.user)
      if (data?.transaction) setTransactions((prev) => [data.transaction].concat(prev))
      setAmount("")
      setReason("")
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const setAmountQuick = (dollars) => setAmount(dollars.toFixed(2))

  const toggleAdminRole = async () => {
    if (!user || roleSaving) return
    setRoleSaving(true)
    try {
      const hasAdmin = (user.roles || []).includes("admin")
      const data = hasAdmin
        ? await apiFetch(`/api/admin/users/${user.id}/roles/admin`, { method: "DELETE" })
        : await apiFetch(`/api/admin/users/${user.id}/roles`, { method: "POST", body: { role: "admin" } })
      if (data?.user) setUser(data.user)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setRoleSaving(false)
    }
  }

  if (!currentUser) {
    return <CenteredMessage title="Admin" body="Log in required." />
  }

  if (!isAdmin) {
    return <CenteredMessage title="Admin" body="You don’t have access to this page." />
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="User"
        subtitle="View and edit roles and balance."
        action={
          <Button component={RouterLink} to="/admin/users" variant="light">
            Back to all users
          </Button>
        }
      />

      <ErrorAlert message={error} />
      {loading && !user ? <LoadingState label="Loading user..." /> : null}

      {user ? (
        <>
          <SectionCard
            title={user.email || `User #${user.id}`}
            subtitle={`${user.email || ""}${user.created_at ? ` • created ${formatDateTime(user.created_at)}` : ""}`}
            action={
              <Group gap="xs">
                <Badge variant="light">#{user.id}</Badge>
                <Badge variant="light" title="Account balance">
                  {formatCurrencyFromCents(user.account_balance_cents ?? 0)}
                </Badge>
              </Group>
            }
          >
            <Group gap="xs">
              {(user.roles || []).length === 0 ? <Text c="dimmed">none</Text> : null}
              {(user.roles || []).map((role) => (
                <Badge key={role} variant="light" color="red">
                  {role}
                </Badge>
              ))}
            </Group>

            <Button color={(user.roles || []).includes("admin") ? "red" : "blue"} variant="light" disabled={roleSaving} onClick={toggleAdminRole}>
              {(user.roles || []).includes("admin") ? "Remove admin role" : "Grant admin role"}
            </Button>
          </SectionCard>

          <SectionCard title="Adjust balance" subtitle="Use a positive dollar amount to add funds, negative to remove.">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Stack gap="sm">
                <TextInput label="Amount" value={amount} placeholder="+25.00" onChange={(event) => setAmount(event.currentTarget.value)} />
                <Group gap="xs">
                  <Button type="button" variant="light" onClick={() => setAmountQuick(25)}>
                    +$25
                  </Button>
                  <Button type="button" variant="light" onClick={() => setAmountQuick(50)}>
                    +$50
                  </Button>
                  <Button type="button" variant="light" onClick={() => setAmountQuick(100)}>
                    +$100
                  </Button>
                  <Button type="button" variant="light" color="red" onClick={() => setAmountQuick(-25)}>
                    -$25
                  </Button>
                </Group>
              </Stack>
              <TextInput label="Reason (optional)" value={reason} placeholder="e.g. onboarding bonus" onChange={(event) => setReason(event.currentTarget.value)} />
            </SimpleGrid>

            <Group justify="flex-end">
              <Button type="button" disabled={saving} onClick={submitBalanceAdjustment}>
                Apply
              </Button>
            </Group>
          </SectionCard>

          <SectionCard title="Balance history" subtitle="Most recent first.">
            <AccountBalanceHistoryTable transactions={transactions} showCreatedBy />
          </SectionCard>
        </>
      ) : null}
    </Stack>
  )
}
